import { isNil } from 'lodash';
import Handsontable from 'handsontable';
import { MNTableContent, TableChangeSource } from '../../../../../../common/table/TableTypes';
import { convertColWidthsGUtoPx } from '../../tableSizeUtils';
import { renderAndResizeHotTable, setHotTableCellMetaObject } from '../hotTableUpdateUtils';

/**
 * Rehydrates the active HOT instance from a raw table snapshot (i.e. the persisted form).
 */
export const applyRawTableContentToHotInstance = ({
    hotInstance,
    tableContent,
    gridSize,
}: {
    hotInstance: Handsontable;
    tableContent: MNTableContent;
    gridSize: number;
}): void => {
    const cellValues = tableContent.data.map((row) => row.map((cellData) => cellData.value));
    const denseCellValues = tableContent.data.map((row) => row.map((cellData) => cellData.value ?? ''));
    const hasBlankCells = cellValues.some((row) => row.some((cellValue) => cellValue === null));
    const colWidthsPx = convertColWidthsGUtoPx(tableContent.colWidthsGU, gridSize);

    hotInstance.suspendRender();

    // The formulas plugin has its own internal representation of the sheet's
    // values, so we need to set it here as well
    const formulasPlugin = hotInstance.getPlugin<'formulas'>('formulas');
    const hasFormulasPlugin = !isNil(formulasPlugin.sheetId) &&
        formulasPlugin.engine?.isItPossibleToReplaceSheetContent?.(formulasPlugin.sheetId, cellValues);

    // Keep the formulas engine aligned with the snapshot we are about to load so `afterLoadData`
    // doesn't read stale serialized formulas from the previous engine state.
    if (hasFormulasPlugin) {
        formulasPlugin.engine!.setSheetContent(formulasPlugin.sheetId, cellValues);
    }

    // `loadData` lets us replace the source snapshot without generating user-edit change hooks.
    hotInstance.loadData(cellValues, TableChangeSource.LOAD_DATA);

    // TODO-TABLES - When handsontable is upgraded (and hyperformula, as a result) double check if this change is
    //  still needed. According to [this issue](https://linear.app/milanote/review/revert-web-14238-fix-issue-with-formula-operations-on-empty-cell-fd0264261e8a)
    //  a fix is in the hyperformula library
    // Handsontable's formulas plugin replaces the engine sheet from the raw HOT source during `afterLoadData`.
    // For sparse persisted tables this can leave blank rows/cells without backing HF entries, which then crashes
    // later structural operations such as column moves. Re-apply a dense version of the snapshot after `loadData`
    // so the engine keeps explicit blank cells while HOT still renders the original `null` source values.
    const canReplaceDenseSheetContent =
        hasBlankCells &&
        !isNil(formulasPlugin.sheetId) &&
        formulasPlugin.engine?.isItPossibleToReplaceSheetContent?.(formulasPlugin.sheetId, denseCellValues);

    if (canReplaceDenseSheetContent) {
        formulasPlugin.engine!.setSheetContent(formulasPlugin.sheetId, denseCellValues);
    }

    tableContent.data.forEach((rowData, row) => {
        rowData.forEach((cellData, col) => {
            setHotTableCellMetaObject(hotInstance, row, col, { cellData });
        });
    });

    const manualColumnResizePlugin = hotInstance.getPlugin<'manualColumnResize'>('manualColumnResize');
    colWidthsPx.forEach((colWidthPx, col) => {
        // Keep Handsontable's manual resize plugin state aligned with the width we just applied.
        manualColumnResizePlugin.newSize = manualColumnResizePlugin.setManualSize(col, colWidthPx);
    });

    hotInstance.resumeRender();
    renderAndResizeHotTable(hotInstance);
};
